import React, { useState, useEffect, useCallback, useRef } from 'react';
import api from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { Switch } from '../components/ui/switch';
import { Separator } from '../components/ui/separator';
import { DataTable } from '../components/ui/data-table';
import { toast } from 'sonner';
import {
  Gauge, Plus, Edit, Trash2, Search, CheckCircle, AlertTriangle, XCircle, Clock,
  FileText, Download, Upload, Filter, Eye, Pen, ChevronRight, ArrowLeft, QrCode, Printer
} from 'lucide-react';
import SignatureCanvas from 'react-signature-canvas';
import * as XLSX from 'xlsx';
import { Checkbox } from '../components/ui/checkbox';

const STATUS_CONFIG = {
  valid: { label: 'Valid', color: 'bg-emerald-100 text-emerald-700' },
  atentie: { label: 'Atentie', color: 'bg-amber-100 text-amber-700' },
  expira_curand: { label: 'Expira curand', color: 'bg-orange-100 text-orange-700' },
  expirat: { label: 'Expirat', color: 'bg-red-100 text-red-700' },
  necunoscut: { label: 'Necunoscut', color: 'bg-slate-100 text-slate-500' }
};

const emptyForm = {
  denumire: '', tip_serie: '', domeniu: '', localizare: '', numar_inventar: '',
  verificat_la: '', interval_verificare: '5 ani', is_active: true, notes: ''
};

const EMMNomenclatorPage = () => {
  const { user, hasRole } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [activeFilter, setActiveFilter] = useState('active'); // active / all / inactive
  const [showDialog, setShowDialog] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [formData, setFormData] = useState(emptyForm);
  const [selectedItems, setSelectedItems] = useState([]);

  // Bulk update dialog
  const [showBulkUpdate, setShowBulkUpdate] = useState(false);
  const [bulkFields, setBulkFields] = useState({});
  const [bulkChecks, setBulkChecks] = useState({});

  // PDF Wizard
  const [showPdfWizard, setShowPdfWizard] = useState(false);
  const [pdfStep, setPdfStep] = useState(1);
  const [pdfData, setPdfData] = useState({ signer_name: '', signer_role: 'Responsabil Metrolog' });
  const sigRef = useRef(null);
  const [signatureDataUrl, setSignatureDataUrl] = useState(null);
  const [pdfGenerating, setPdfGenerating] = useState(false);

  // Excel import
  const fileInputRef = useRef(null);
  const [importing, setImporting] = useState(false);

  // QR state
  const [showQRDialog, setShowQRDialog] = useState(false);
  const [qrLoading, setQrLoading] = useState(false);
  const [showQRPreview, setShowQRPreview] = useState(null);
  const [qrPreviewData, setQrPreviewData] = useState(null);
  const [qrFields, setQrFields] = useState({
    denumire: true,
    tip_serie: true,
    numar_inventar: true,
    domeniu: true,
    localizare: false,
    interval_verificare: false,
  });

  // EMM Settings (for PDF pre-fill)
  const [emmSettings, setEmmSettings] = useState(null);

  const handleSelectionChange = useCallback((sel) => setSelectedItems(sel), []);

  const fetchItems = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (activeFilter === 'active') params.is_active = true;
      else if (activeFilter === 'inactive') params.is_active = false;
      const response = await api.get('/emm/equipment', { params });
      setItems(response.data);
    } catch (error) {
      toast.error('Eroare la incarcarea datelor');
    } finally {
      setLoading(false);
    }
  }, [activeFilter]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  useEffect(() => {
    api.get('/emm/settings').then(res => setEmmSettings(res.data)).catch(() => {});
  }, []);

  // CRUD
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const uppercasedData = {
        ...formData,
        denumire: formData.denumire.toUpperCase(),
        tip_serie: formData.tip_serie.toUpperCase(),
        domeniu: formData.domeniu.toUpperCase(),
        localizare: formData.localizare.toUpperCase(),
        numar_inventar: formData.numar_inventar.toUpperCase(),
        notes: formData.notes.toUpperCase()
      };
      if (editItem) {
        await api.put(`/emm/equipment/${editItem.id}`, uppercasedData);
        toast.success('Echipament EMM actualizat!');
      } else {
        await api.post('/emm/equipment', uppercasedData);
        toast.success('Echipament EMM adaugat!');
      }
      setShowDialog(false);
      setEditItem(null);
      setFormData(emptyForm);
      fetchItems();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Eroare la salvare');
    }
  };

  const handleEdit = (item) => {
    setEditItem(item);
    setFormData({
      denumire: item.denumire || '',
      tip_serie: item.tip_serie || '',
      domeniu: item.domeniu || '',
      localizare: item.localizare || '',
      numar_inventar: item.numar_inventar || '',
      verificat_la: item.verificat_la || '',
      interval_verificare: item.interval_verificare || '5 ani',
      is_active: item.is_active !== false,
      notes: item.notes || ''
    });
    setShowDialog(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Sigur doriti sa stergeti acest echipament EMM?')) return;
    try {
      await api.delete(`/emm/equipment/${id}`);
      toast.success('Sters!');
      fetchItems();
    } catch (error) {
      toast.error('Eroare la stergere');
    }
  };

  // Bulk actions
  const handleBulkUpdate = async () => {
    const fields = {};
    Object.keys(bulkChecks).forEach(key => {
      if (bulkChecks[key]) fields[key] = bulkFields[key];
    });
    if (Object.keys(fields).length === 0) { toast.error('Selectati cel putin un camp'); return; }
    try {
      const ids = Array.isArray(selectedItems) ? selectedItems.map(i => typeof i === 'string' ? i : i.id) : [];
      await api.patch('/emm/equipment/bulk-update-fields', { ids, fields });
      toast.success(`${ids.length} echipamente actualizate!`);
      setShowBulkUpdate(false);
      setBulkFields({});
      setBulkChecks({});
      fetchItems();
    } catch (error) {
      toast.error('Eroare la actualizare in masa');
    }
  };

  const handleBulkDelete = async () => {
    if (!window.confirm(`Sigur doriti sa stergeti ${selectedItems.length} echipamente?`)) return;
    try {
      const ids = Array.isArray(selectedItems) ? selectedItems.map(i => typeof i === 'string' ? i : i.id) : [];
      await api.delete('/emm/equipment/bulk-delete', { data: { ids } });
      toast.success(`${ids.length} echipamente sterse!`);
      fetchItems();
    } catch (error) {
      toast.error('Eroare la stergere in masa');
    }
  };

  // PDF Generation
  const handleGeneratePdf = async () => {
    if (!signatureDataUrl) {
      toast.error('Semnatura este obligatorie');
      return;
    }
    try {
      setPdfGenerating(true);
      const payload = {
        signature: signatureDataUrl,
        signer_name: pdfData.signer_name,
        signer_role: pdfData.signer_role
      };
      const response = await api.post('/emm/generate-pdf', payload, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Lista_EMM_${new Date().toISOString().slice(0, 10)}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('PDF generat cu succes!');
      setShowPdfWizard(false);
      setPdfStep(1);
      setSignatureDataUrl(null);
    } catch (error) {
      toast.error('Eroare la generare PDF');
    } finally {
      setPdfGenerating(false);
    }
  };

  // Excel Export
  const handleExportExcel = () => {
    const exportData = filteredItems.map((item, idx) => ({
      'Nr. Crt.': idx + 1,
      'Denumire Echipament': (item.denumire || '').toUpperCase(),
      'Tip / Seria': (item.tip_serie || '').toUpperCase(),
      'Domeniu': (item.domeniu || '').toUpperCase(),
      'Nr. Inventar': (item.numar_inventar || '').toUpperCase(),
      'Localizare': (item.localizare || '').toUpperCase(),
      'Verificat La': item.verificat_la ? new Date(item.verificat_la).toLocaleDateString('ro-RO') : '',
      'Verificat Pana La': item.verificat_pana_la ? new Date(item.verificat_pana_la).toLocaleDateString('ro-RO') : '',
      'Interval Verificare': (item.interval_verificare || '').toUpperCase(),
      'Status': STATUS_CONFIG[item.verification_status]?.label || 'N/A',
      'Activ': item.is_active ? 'Da' : 'Nu',
      'Note': (item.notes || '').toUpperCase()
    }));
    const ws = XLSX.utils.json_to_sheet(exportData);
    const colWidths = [8, 35, 25, 25, 16, 22, 14, 14, 16, 12, 8, 25];
    ws['!cols'] = colWidths.map(w => ({ wch: w }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Nomenclator EMM');
    XLSX.writeFile(wb, `Nomenclator_EMM_${new Date().toISOString().slice(0, 10)}.xlsx`);
    toast.success('Export Excel realizat cu succes!');
  };

  // Excel Import
  const handleImportExcel = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    try {
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data);
      const sheet = workbook.Sheets[workbook.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(sheet);
      if (rows.length === 0) { toast.error('Fisierul Excel nu contine date'); setImporting(false); return; }

      let imported = 0;
      let errors = 0;
      for (const row of rows) {
        const denumire = (row['Denumire Echipament'] || row['denumire'] || row['Denumire'] || '').toString().trim().toUpperCase();
        if (!denumire) { errors++; continue; }
        const payload = {
          denumire,
          tip_serie: (row['Tip / Seria'] || row['tip_serie'] || row['Tip'] || '').toString().trim().toUpperCase(),
          domeniu: (row['Domeniu'] || row['domeniu'] || '').toString().trim().toUpperCase(),
          numar_inventar: (row['Nr. Inventar'] || row['numar_inventar'] || row['Nr Inventar'] || '').toString().trim().toUpperCase(),
          localizare: (row['Localizare'] || row['localizare'] || '').toString().trim().toUpperCase(),
          interval_verificare: (row['Interval Verificare'] || row['interval_verificare'] || row['Interval'] || '5 ani').toString().trim(),
          verificat_la: parseExcelDate(row['Verificat La'] || row['verificat_la'] || ''),
          is_active: true,
          notes: (row['Note'] || row['notes'] || '').toString().trim().toUpperCase()
        };
        try {
          await api.post('/emm/equipment', payload);
          imported++;
        } catch { errors++; }
      }
      toast.success(`Import finalizat: ${imported} echipamente importate${errors > 0 ? `, ${errors} erori` : ''}`);
      fetchItems();
    } catch (error) {
      toast.error('Eroare la citirea fisierului Excel');
    } finally {
      setImporting(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const parseExcelDate = (val) => {
    if (!val) return '';
    const s = val.toString().trim();
    // Try DD.MM.YYYY format
    const parts = s.match(/^(\d{1,2})[./](\d{1,2})[./](\d{4})$/);
    if (parts) return `${parts[3]}-${parts[2].padStart(2, '0')}-${parts[1].padStart(2, '0')}`;
    // Try YYYY-MM-DD format
    if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
    // Excel serial number
    if (!isNaN(s) && Number(s) > 30000) {
      const d = new Date((Number(s) - 25569) * 86400 * 1000);
      return d.toISOString().slice(0, 10);
    }
    return '';
  };

  // QR Handlers
  const qrFieldOptions = [
    { key: 'denumire', label: 'Denumire echipament' },
    { key: 'tip_serie', label: 'Tip / Seria' },
    { key: 'numar_inventar', label: 'Nr. Inventar' },
    { key: 'domeniu', label: 'Domeniu' },
    { key: 'localizare', label: 'Localizare' },
    { key: 'interval_verificare', label: 'Interval verificare' },
  ];

  const handleGenerateQR = async (format) => {
    setQrLoading(true);
    try {
      const fieldsStr = Object.keys(qrFields).filter(k => qrFields[k]).join(',');
      const ids = selectedItems.length > 0 ? selectedItems.map(i => typeof i === 'string' ? i : i.id).join(',') : '';
      let url = `/emm/export/qr-codes?format=${format}&fields=${fieldsStr}`;
      if (ids) url += `&equipment_ids=${ids}`;
      const res = await api.get(url, { responseType: 'blob' });
      const blobUrl = URL.createObjectURL(res.data);
      const a = document.createElement('a'); a.href = blobUrl; a.download = `qr_emm_${format}.pdf`; a.click();
      URL.revokeObjectURL(blobUrl);
      setShowQRDialog(false);
      toast.success(`Export QR (${format}) generat!`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Eroare la generare QR');
    } finally { setQrLoading(false); }
  };

  const handleExportQrZip = async () => {
    setQrLoading(true);
    try {
      const fieldsStr = Object.keys(qrFields).filter(k => qrFields[k]).join(',');
      const ids = selectedItems.length > 0 ? selectedItems.map(i => typeof i === 'string' ? i : i.id).join(',') : '';
      let url = `/emm/export/qr-zip?fields=${fieldsStr}`;
      if (ids) url += `&equipment_ids=${ids}`;
      const res = await api.get(url, { responseType: 'blob' });
      const blobUrl = URL.createObjectURL(res.data);
      const a = document.createElement('a'); a.href = blobUrl; a.download = `qr_emm.zip`; a.click();
      URL.revokeObjectURL(blobUrl);
      setShowQRDialog(false);
      toast.success('Arhiva ZIP cu imagini QR generata!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Eroare la export ZIP');
    } finally { setQrLoading(false); }
  };

  const handlePreviewQR = async (item) => {
    setShowQRPreview(item);
    setQrPreviewData(null);
    try {
      const res = await api.get(`/emm/qr-preview/${item.id}`);
      setQrPreviewData(res.data);
    } catch { toast.error('Eroare la generare previzualizare QR'); }
  };

  // Filter data
  const filteredItems = items.filter(item => {
    if (statusFilter !== 'all' && item.verification_status !== statusFilter) return false;
    if (searchTerm) {
      const s = searchTerm.toLowerCase();
      if (!(item.denumire?.toLowerCase().includes(s) || item.tip_serie?.toLowerCase().includes(s) || item.numar_inventar?.toLowerCase().includes(s) || item.localizare?.toLowerCase().includes(s))) return false;
    }
    return true;
  });

  const tableData = filteredItems.map(item => ({
    ...item,
    denumire: (item.denumire || '').toUpperCase(),
    tip_serie: (item.tip_serie || '').toUpperCase(),
    domeniu: (item.domeniu || '').toUpperCase(),
    localizare: (item.localizare || '').toUpperCase(),
    status_display: STATUS_CONFIG[item.verification_status]?.label || 'N/A',
    verificat_la_display: item.verificat_la ? new Date(item.verificat_la).toLocaleDateString('ro-RO') : '-',
    verificat_display: item.verificat_pana_la ? new Date(item.verificat_pana_la).toLocaleDateString('ro-RO') : '-',
    days_display: item.days_until_expiry !== null ? `${item.days_until_expiry} zile` : '-',
    active_display: item.is_active ? 'Da' : 'Nu'
  }));

  const columns = [
    {
      key: 'denumire', label: 'Denumire Echipament', sortable: true, filterable: true,
      render: (v, row) => (
        <div>
          <p className="font-medium text-slate-900">{v}</p>
          {!row.is_active && <Badge variant="outline" className="text-xs mt-0.5 text-slate-400">Dezactivat</Badge>}
        </div>
      )
    },
    { key: 'tip_serie', label: 'Tip / Seria', sortable: true, filterable: true },
    { key: 'domeniu', label: 'Domeniu', sortable: true, filterable: true },
    { key: 'numar_inventar', label: 'Nr. Inventar', sortable: true, filterable: true, width: 'w-28',
      render: (v) => v ? <span className="font-mono text-xs font-semibold text-indigo-700 bg-indigo-50 px-1.5 py-0.5 rounded">{v}</span> : <span className="text-slate-400">-</span>
    },
    { key: 'localizare', label: 'Localizare', sortable: true, filterable: true },
    {
      key: 'status_display', label: 'Status', sortable: true, filterable: true, width: 'w-28',
      render: (v, row) => {
        const cfg = STATUS_CONFIG[row.verification_status] || STATUS_CONFIG.necunoscut;
        return <Badge className={cfg.color}>{cfg.label}</Badge>;
      }
    },
    { key: 'verificat_la_display', label: 'Verificat la', sortable: true, width: 'w-28' },
    { key: 'verificat_display', label: 'Verificat pana la', sortable: true, width: 'w-32' },
    { key: 'interval_verificare', label: 'Interval', sortable: true, width: 'w-24' },
    {
      key: 'actions', label: '', sortable: false, filterable: false, width: 'w-24',
      render: (_, row) => (
        <div className="flex items-center gap-1" onClick={e => e.stopPropagation()}>
          <Button variant="ghost" size="sm" onClick={() => handlePreviewQR(row)} title="Cod QR"><QrCode className="w-4 h-4 text-blue-600" /></Button>
          <Button variant="ghost" size="sm" onClick={() => handleEdit(row)} title="Editeaza"><Edit className="w-4 h-4" /></Button>
          <Button variant="ghost" size="sm" onClick={() => handleDelete(row.id)} title="Sterge" className="text-red-500"><Trash2 className="w-4 h-4" /></Button>
        </div>
      )
    }
  ];

  return (
    <div className="space-y-6" data-testid="emm-nomenclator-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl lg:text-4xl font-bold text-slate-900">Nomenclator EMM</h1>
          <p className="text-slate-600 mt-1">Echipamente de Masurare si Monitorizare</p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          {/* QR Export */}
          <Button
            variant="outline"
            className="h-12 border-blue-300 text-blue-700 hover:bg-blue-50"
            onClick={() => setShowQRDialog(true)}
            data-testid="export-qr-btn"
          >
            <QrCode className="w-4 h-4 mr-2" />
            Export QR
            {selectedItems.length > 0 && <Badge className="ml-1.5 bg-blue-600 text-white text-[10px] px-1.5 py-0 h-5">{selectedItems.length}</Badge>}
          </Button>
          {/* Excel Export/Import */}
          <Button
            variant="outline"
            className="h-12 border-emerald-300 text-emerald-700 hover:bg-emerald-50"
            onClick={handleExportExcel}
            data-testid="export-excel-btn"
          >
            <Download className="w-4 h-4 mr-2" />
            Export Excel
          </Button>
          <div>
            <input
              type="file"
              ref={fileInputRef}
              accept=".xlsx,.xls,.csv"
              onChange={handleImportExcel}
              className="hidden"
              data-testid="import-excel-input"
            />
            <Button
              variant="outline"
              className="h-12 border-blue-300 text-blue-700 hover:bg-blue-50"
              onClick={() => fileInputRef.current?.click()}
              disabled={importing}
              data-testid="import-excel-btn"
            >
              <Upload className="w-4 h-4 mr-2" />
              {importing ? 'Se importa...' : 'Import Excel'}
            </Button>
          </div>

          {/* PDF Generation Button */}
          <Button
            className="h-12 px-6 bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white shadow-lg"
            onClick={() => {
              setShowPdfWizard(true); setPdfStep(1); setSignatureDataUrl(null);
              setPdfData({
                signer_name: emmSettings?.responsabil_nume || user?.full_name || '',
                signer_role: emmSettings?.responsabil_functie || 'Responsabil Metrolog'
              });
            }}
            data-testid="generate-pdf-btn"
          >
            <FileText className="w-5 h-5 mr-2" />
            Genereaza Lista PDF
          </Button>

          {selectedItems.length > 0 && (
            <>
              <Button variant="outline" className="h-12" onClick={() => { setShowBulkUpdate(true); setBulkFields({}); setBulkChecks({}); }}>
                <Edit className="w-4 h-4 mr-2" />
                Actualizare in masa ({selectedItems.length})
              </Button>
              <Button variant="outline" className="h-12 text-red-600 border-red-200 hover:bg-red-50" onClick={handleBulkDelete}>
                <Trash2 className="w-4 h-4 mr-2" />
                Stergere in masa ({selectedItems.length})
              </Button>
            </>
          )}

          <Dialog open={showDialog} onOpenChange={(open) => { setShowDialog(open); if (!open) { setEditItem(null); setFormData(emptyForm); } }}>
            <DialogTrigger asChild>
              <Button className="btn-primary h-12" data-testid="add-emm-btn">
                <Plus className="w-5 h-5 mr-2" />
                ADAUGA EMM
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{editItem ? 'Editeaza Echipament EMM' : 'Adauga Echipament EMM'}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2 col-span-2">
                    <Label>Denumire echipament *</Label>
                    <Input value={formData.denumire} onChange={(e) => setFormData({ ...formData, denumire: e.target.value })} required className="h-11" placeholder="ex: Subler cu cadran UNIOR" data-testid="emm-denumire-input" />
                  </div>
                  <div className="space-y-2">
                    <Label>Tip / Seria</Label>
                    <Input value={formData.tip_serie} onChange={(e) => setFormData({ ...formData, tip_serie: e.target.value })} className="h-11" placeholder="ex: Serie: SC15603182561" />
                  </div>
                  <div className="space-y-2">
                    <Label>Domeniu</Label>
                    <Input value={formData.domeniu} onChange={(e) => setFormData({ ...formData, domeniu: e.target.value })} className="h-11" placeholder="ex: 0.01mm, 0-150 mm" />
                  </div>
                  <div className="space-y-2">
                    <Label>Nr. Inventar</Label>
                    <Input value={formData.numar_inventar} onChange={(e) => setFormData({ ...formData, numar_inventar: e.target.value })} className="h-11 border-indigo-200 focus:border-indigo-400" placeholder="ex: INV-00123" data-testid="emm-numar-inventar-input" />
                  </div>
                  <div className="space-y-2">
                    <Label>Localizare</Label>
                    <Input value={formData.localizare} onChange={(e) => setFormData({ ...formData, localizare: e.target.value })} className="h-11" placeholder="ex: Responsabil Metrolog" />
                  </div>
                  <div className="space-y-2">
                    <Label>Interval verificare</Label>
                    <Select value={formData.interval_verificare} onValueChange={(v) => setFormData({ ...formData, interval_verificare: v })}>
                      <SelectTrigger className="h-11"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="1 an">1 an</SelectItem>
                        <SelectItem value="2 ani">2 ani</SelectItem>
                        <SelectItem value="3 ani">3 ani</SelectItem>
                        <SelectItem value="5 ani">5 ani</SelectItem>
                        <SelectItem value="10 ani">10 ani</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Verificat la (data ultimei verificari)</Label>
                    <Input type="date" value={formData.verificat_la} onChange={(e) => setFormData({ ...formData, verificat_la: e.target.value })} className="h-11" />
                  </div>
                  <div className="space-y-2 flex items-center gap-3">
                    <Switch checked={formData.is_active} onCheckedChange={(v) => setFormData({ ...formData, is_active: v })} id="emm-active" />
                    <Label htmlFor="emm-active">Activ</Label>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Note</Label>
                  <Textarea value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} rows={2} placeholder="Observatii..." />
                </div>
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>Anuleaza</Button>
                  <Button type="submit" className="btn-primary" data-testid="save-emm-btn">{editItem ? 'ACTUALIZEAZA' : 'ADAUGA'}</Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-4 items-start md:items-center">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
          <Input placeholder="Cauta echipament EMM..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-10 h-12" data-testid="search-emm-input" />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-full md:w-44 h-12"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Toate statusurile</SelectItem>
            <SelectItem value="valid">Valid</SelectItem>
            <SelectItem value="atentie">Atentie</SelectItem>
            <SelectItem value="expira_curand">Expira curand</SelectItem>
            <SelectItem value="expirat">Expirat</SelectItem>
          </SelectContent>
        </Select>
        <Select value={activeFilter} onValueChange={setActiveFilter}>
          <SelectTrigger className="w-full md:w-40 h-12"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="active">Doar active</SelectItem>
            <SelectItem value="all">Toate</SelectItem>
            <SelectItem value="inactive">Dezactivate</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0052CC]"></div>
        </div>
      ) : (
        <DataTable
          data={tableData}
          columns={columns}
          pageSize={20}
          searchable={false}
          exportable={true}
          printable={true}
          selectable={true}
          onSelectionChange={handleSelectionChange}
          emptyMessage="Nu exista echipamente EMM"
        />
      )}

      {/* Bulk Update Dialog */}
      <Dialog open={showBulkUpdate} onOpenChange={setShowBulkUpdate}>
        <DialogContent>
          <DialogHeader><DialogTitle>Actualizare in masa ({selectedItems.length} echipamente)</DialogTitle></DialogHeader>
          <div className="space-y-4">
            {[
              { key: 'localizare', label: 'Localizare', type: 'input' },
              { key: 'numar_inventar', label: 'Nr. Inventar', type: 'input' },
              { key: 'interval_verificare', label: 'Interval verificare', type: 'select', options: ['1 an', '2 ani', '3 ani', '5 ani', '10 ani'] },
              { key: 'verificat_la', label: 'Verificat la (data verificarii)', type: 'date' },
              { key: 'is_active', label: 'Activ', type: 'switch' }
            ].map(field => (
              <div key={field.key} className="flex items-center gap-3">
                <input type="checkbox" checked={bulkChecks[field.key] || false} onChange={(e) => setBulkChecks({ ...bulkChecks, [field.key]: e.target.checked })} className="w-4 h-4" />
                <div className="flex-1">
                  <Label className="text-sm">{field.label}</Label>
                  {field.type === 'input' && <Input value={bulkFields[field.key] || ''} onChange={(e) => setBulkFields({ ...bulkFields, [field.key]: e.target.value })} className="h-9 mt-1" disabled={!bulkChecks[field.key]} />}
                  {field.type === 'date' && <Input type="date" value={bulkFields[field.key] || ''} onChange={(e) => setBulkFields({ ...bulkFields, [field.key]: e.target.value })} className="h-9 mt-1" disabled={!bulkChecks[field.key]} />}
                  {field.type === 'select' && (
                    <Select value={bulkFields[field.key] || ''} onValueChange={(v) => setBulkFields({ ...bulkFields, [field.key]: v })} disabled={!bulkChecks[field.key]}>
                      <SelectTrigger className="h-9 mt-1"><SelectValue placeholder="Selecteaza..." /></SelectTrigger>
                      <SelectContent>{field.options.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                    </Select>
                  )}
                  {field.type === 'switch' && (
                    <div className="mt-1"><Switch checked={bulkFields[field.key] || false} onCheckedChange={(v) => setBulkFields({ ...bulkFields, [field.key]: v })} disabled={!bulkChecks[field.key]} /></div>
                  )}
                </div>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBulkUpdate(false)}>Anuleaza</Button>
            <Button className="btn-primary" onClick={handleBulkUpdate}>ACTUALIZEAZA</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* PDF Wizard Dialog */}
      <Dialog open={showPdfWizard} onOpenChange={(open) => { setShowPdfWizard(open); if (!open) setPdfStep(1); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-red-600" />
              Genereaza Lista EMM-uri PDF
              <Badge variant="outline" className="ml-2">Pas {pdfStep}/2</Badge>
            </DialogTitle>
          </DialogHeader>

          {pdfStep === 1 && (
            <div className="space-y-4" data-testid="pdf-wizard-step1">
              <div className="space-y-2">
                <Label>Nume complet semnatarul *</Label>
                <Input value={pdfData.signer_name} onChange={(e) => setPdfData({ ...pdfData, signer_name: e.target.value })} className="h-11" placeholder="ex: Dutu Valentin" />
              </div>
              <div className="space-y-2">
                <Label>Functia</Label>
                <Input value={pdfData.signer_role} onChange={(e) => setPdfData({ ...pdfData, signer_role: e.target.value })} className="h-11" placeholder="ex: Responsabil Metrolog" />
              </div>

              <Separator />

              <div className="space-y-2">
                <Label className="font-semibold">Semnatura digitala *</Label>
                <div className="border-2 border-slate-300 rounded-lg bg-white relative" style={{ height: '160px' }}>
                  <SignatureCanvas
                    ref={sigRef}
                    penColor="black"
                    minWidth={1.5}
                    maxWidth={3}
                    canvasProps={{
                      width: 440,
                      height: 156,
                      className: 'rounded-lg',
                      style: { width: '100%', height: '100%', borderRadius: '0.5rem' }
                    }}
                    backgroundColor="rgb(255,255,255)"
                  />
                  <div className="absolute bottom-2 left-0 right-0 border-t border-dashed border-slate-300 mx-6"></div>
                  <span className="absolute bottom-1 right-3 text-[10px] text-slate-400">Semnati aici</span>
                </div>
                <Button variant="ghost" size="sm" onClick={() => sigRef.current?.clear()}>
                  <Trash2 className="w-3 h-3 mr-1" /> Sterge semnatura
                </Button>
              </div>
            </div>
          )}

          {pdfStep === 2 && (
            <div className="space-y-4 text-center py-6" data-testid="pdf-wizard-step2">
              {pdfGenerating ? (
                <>
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto"></div>
                  <p className="text-slate-600">Se genereaza PDF-ul...</p>
                  <p className="text-sm text-slate-400">Contine {items.filter(i => i.is_active !== false).length} echipamente EMM active</p>
                </>
              ) : (
                <>
                  <CheckCircle className="w-16 h-16 text-emerald-500 mx-auto" />
                  <p className="text-lg font-semibold text-slate-900">Gata de generare!</p>
                  <p className="text-sm text-slate-500">
                    Lista va contine {items.filter(i => i.is_active !== false).length} echipamente EMM active
                  </p>
                  <p className="text-sm text-slate-500">Semnat de: {pdfData.signer_name} - {pdfData.signer_role}</p>
                </>
              )}
            </div>
          )}

          <DialogFooter>
            {pdfStep === 1 && (
              <>
                <Button variant="outline" onClick={() => setShowPdfWizard(false)}>Anuleaza</Button>
                <Button className="btn-primary" onClick={() => {
                  if (!pdfData.signer_name) { toast.error('Numele semnatarului este obligatoriu'); return; }
                  if (!sigRef.current || sigRef.current.isEmpty()) { toast.error('Semnatura este obligatorie'); return; }
                  setSignatureDataUrl(sigRef.current.toDataURL('image/png'));
                  setPdfStep(2);
                }} data-testid="pdf-next-step-btn">
                  Pasul Urmator <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </>
            )}
            {pdfStep === 2 && (
              <>
                <Button variant="outline" onClick={() => setPdfStep(1)} disabled={pdfGenerating}>
                  <ArrowLeft className="w-4 h-4 mr-1" /> Inapoi
                </Button>
                <Button
                  className="bg-red-600 hover:bg-red-700 text-white"
                  onClick={handleGeneratePdf}
                  disabled={pdfGenerating}
                  data-testid="generate-pdf-confirm-btn"
                >
                  <Download className="w-4 h-4 mr-2" />
                  {pdfGenerating ? 'Se genereaza...' : 'GENEREAZA PDF'}
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* QR Export Dialog */}
      <Dialog open={showQRDialog} onOpenChange={setShowQRDialog}>
        <DialogContent className="max-w-md" data-testid="emm-qr-generate-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <QrCode className="w-5 h-5 text-blue-600" />
              Export Coduri QR - EMM
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-sm text-slate-500">
              Se vor exporta {selectedItems.length > 0 ? selectedItems.length : filteredItems.length} echipamente EMM
              {selectedItems.length > 0 && <span className="text-blue-600 font-medium"> (selectie)</span>}
            </p>

            <div className="space-y-3">
              <p className="text-sm font-medium text-slate-700">Campuri afisate pe eticheta:</p>
              <div className="grid grid-cols-2 gap-2">
                {qrFieldOptions.map(field => (
                  <div key={field.key} className="flex items-center space-x-2">
                    <Checkbox
                      id={`emm-qr-field-${field.key}`}
                      checked={qrFields[field.key]}
                      onCheckedChange={(checked) => setQrFields({ ...qrFields, [field.key]: checked })}
                      data-testid={`emm-qr-field-${field.key}`}
                    />
                    <label htmlFor={`emm-qr-field-${field.key}`} className="text-sm cursor-pointer">{field.label}</label>
                  </div>
                ))}
              </div>
            </div>

            <Separator />

            <div className="grid gap-3">
              <Button variant="outline" className="h-auto py-4 justify-start border-amber-200 bg-amber-50 hover:bg-amber-100" onClick={() => handleGenerateQR('industrial')} disabled={qrLoading} data-testid="emm-qr-format-industrial">
                <div className="text-left">
                  <p className="font-semibold">Industrial (16/pagina)</p>
                  <p className="text-xs text-slate-500">2 coloane x 8 randuri - 105x38mm etichete industriale</p>
                </div>
              </Button>
              <Button variant="outline" className="h-auto py-4 justify-start" onClick={() => handleGenerateQR('standard')} disabled={qrLoading} data-testid="emm-qr-format-standard">
                <div className="text-left">
                  <p className="font-semibold">Standard (24/pagina)</p>
                  <p className="text-xs text-slate-500">3 coloane x 8 randuri - ideal pentru A4</p>
                </div>
              </Button>
              <Button variant="outline" className="h-auto py-4 justify-start" onClick={() => handleGenerateQR('large')} disabled={qrLoading} data-testid="emm-qr-format-large">
                <div className="text-left">
                  <p className="font-semibold">Mari (8/pagina)</p>
                  <p className="text-xs text-slate-500">2 coloane x 4 randuri - etichete mari vizibile</p>
                </div>
              </Button>
              <Button variant="outline" className="h-auto py-4 justify-start" onClick={() => handleGenerateQR('thermal')} disabled={qrLoading} data-testid="emm-qr-format-thermal">
                <div className="text-left">
                  <p className="font-semibold">Termic (50x30mm)</p>
                  <p className="text-xs text-slate-500">Pentru imprimante de etichete termice</p>
                </div>
              </Button>
              <Separator className="my-2" />
              <Button variant="outline" className="h-auto py-4 justify-start border-blue-200 hover:bg-blue-50" onClick={handleExportQrZip} disabled={qrLoading} data-testid="emm-qr-format-zip">
                <div className="text-left">
                  <p className="font-semibold text-blue-700">Export JPEG (ZIP)</p>
                  <p className="text-xs text-slate-500">Imagini individuale cu campurile selectate</p>
                </div>
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* QR Preview Dialog */}
      <Dialog open={!!showQRPreview} onOpenChange={o => { if (!o) { setShowQRPreview(null); setQrPreviewData(null); } }}>
        <DialogContent className="max-w-sm" data-testid="emm-qr-preview-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <QrCode className="w-5 h-5 text-blue-600" /> Cod QR Echipament EMM
            </DialogTitle>
          </DialogHeader>
          {showQRPreview && (
            <div className="space-y-4">
              <div className="bg-slate-50 rounded-lg p-3 border text-center">
                <p className="font-semibold text-slate-800">{showQRPreview.denumire}</p>
                {showQRPreview.tip_serie && <p className="text-sm font-mono text-slate-600 mt-0.5">{showQRPreview.tip_serie}</p>}
                {showQRPreview.numar_inventar && <p className="text-xs font-semibold text-indigo-700 mt-0.5">Inv: {showQRPreview.numar_inventar}</p>}
              </div>
              {qrPreviewData ? (
                <div className="flex flex-col items-center gap-3">
                  <img
                    src={`data:image/png;base64,${qrPreviewData.qr_base64}`}
                    alt={`QR EMM`}
                    className="w-48 h-48 border rounded-lg"
                    data-testid="emm-qr-preview-image"
                  />
                  <p className="text-xs font-mono text-slate-500 bg-slate-100 px-3 py-1 rounded max-w-full truncate">{qrPreviewData.qr_content}</p>
                  <Button variant="outline" size="sm" className="text-xs" onClick={() => {
                    const link = document.createElement('a');
                    link.href = `data:image/png;base64,${qrPreviewData.qr_base64}`;
                    link.download = `qr_emm_${(showQRPreview.denumire || 'unknown').replace(/\s/g, '_')}.png`;
                    link.click();
                  }} data-testid="emm-qr-download-png-btn">
                    <Download className="w-3.5 h-3.5 mr-1" /> Descarca PNG
                  </Button>
                </div>
              ) : (
                <div className="flex justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowQRPreview(null); setQrPreviewData(null); }}>Inchide</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EMMNomenclatorPage;
